import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { SidebarContext } from "../../../context/Sidebar";
import { LabelFieldComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast, Toaster } from 'react-hot-toast';

export default function AppointmentCreatePage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [pets, setPets] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [breeds, setBreeds] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const { setSidebar } = useContext(SidebarContext);

    // Pet Modal States
    const [showPetModal, setShowPetModal] = useState(false);
    const [petForm, setPetForm] = useState({
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        gender: '',
        birth_date: '',
        is_dead: false,
        death_date: ''
    });
    const [petErrors, setPetErrors] = useState({});
    const [petSubmitting, setPetSubmitting] = useState(false);
    const [newlyCreatedCustomerId, setNewlyCreatedCustomerId] = useState(null);
    const [newlyCreatedBreedId, setNewlyCreatedBreedId] = useState(null);

    // Breed Modal States
    const [showBreedModal, setShowBreedModal] = useState(false);
    const [breedForm, setBreedForm] = useState({
        name: '',
        parent_id: ''
    });
    const [breedErrors, setBreedErrors] = useState({});
    const [breedSubmitting, setBreedSubmitting] = useState(false);

    // Customer Modal States
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerForm, setCustomerForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [customerErrors, setCustomerErrors] = useState({});
    const [customerSubmitting, setCustomerSubmitting] = useState(false);

    // Procedure Modal States
    const [showProcedureModal, setShowProcedureModal] = useState(false);
    const [procedureForm, setProcedureForm] = useState({
        name: '',
        price: ''
    });
    const [procedureErrors, setProcedureErrors] = useState({});
    const [procedureSubmitting, setProcedureSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        pet_id: "",
        doctor_id: "",
        appointment_date: new Date().toISOString().split("T")[0],
        appointment_time_slot: "",
        procedures: [{ procedure_id: "", price: 0 }],
        notes: [""],
        payments: [{ method_id: "", amount: 0, manuallyEdited: false }],
    });

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Auto-select doctor if user is a doctor
    useEffect(() => {
        if (doctors.length > 0) {
            const role = localStorage.getItem("role");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            if (role == "Doctor" && user.id) {
                const matchingDoctor = doctors.find(doctor => doctor.id === user.id);
                if (matchingDoctor) {
                    const doctorOption = {
                        value: matchingDoctor.id,
                        label: matchingDoctor.name
                    };
                    setSelectedDoctor(doctorOption);
                    setFormData(prev => ({
                        ...prev,
                        doctor_id: matchingDoctor.id
                    }));
                }
            }
        }
    }, [doctors]);

    // Helper function to sort time slots - running first, then upcoming, then past
    const sortTimeSlots = (slots, appointmentDate) => {
        const now = new Date();
        const selectedDate = new Date(appointmentDate);
        const isToday = selectedDate.toDateString() === now.toDateString();

        if (!isToday) {
            // If it's not today, return slots in original order (all are future)
            return slots;
        }

        // Categorize slots as past, current, or upcoming
        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();

        const past = [];
        const current = [];
        const upcoming = [];

        slots.forEach(slot => {
            const timeMatch = slot.value.match(/(\d{1,2}):(\d{2})\s(AM|PM)/);
            if (!timeMatch) {
                upcoming.push(slot);
                return;
            }

            let hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const ampm = timeMatch[3];

            // Convert to 24-hour format
            if (ampm === 'PM' && hours !== 12) {
                hours += 12;
            } else if (ampm === 'AM' && hours === 12) {
                hours = 0;
            }

            const slotStartMinutes = hours * 60 + minutes;
            const slotEndMinutes = slotStartMinutes + 30;
            const nowMinutes = currentHours * 60 + currentMinutes;

            if (nowMinutes < slotStartMinutes) {
                // Upcoming
                upcoming.push(slot);
            } else if (nowMinutes >= slotStartMinutes && nowMinutes < slotEndMinutes) {
                // Currently running
                current.push(slot);
            } else {
                // Past
                past.push(slot);
            }
        });

        // Return: current first, then upcoming, then past
        return [...current, ...upcoming, ...past];
    };

    useEffect(() => {
        if (selectedDoctor && formData.appointment_date) {
            // Find the doctor by either top-level ID or nested doctor ID
            const doctor = doctors.find(
                d => d.id === selectedDoctor.value || d.doctor?.id === selectedDoctor.value
            );

            if (doctor) {
                // Get availabilities from the nested doctor object
                const availabilities = doctor.doctor?.availabilities || [];

                console.log('Found doctor:', doctor.name);
                console.log('Availabilities:', availabilities);

                if (availabilities.length > 0) {
                    const date = new Date(formData.appointment_date);
                    const dayName = date.toLocaleDateString("en-US", {
                        weekday: "long",
                    });

                    const matchingAvailabilities = availabilities.filter(
                        avail => avail.day_of_week === dayName
                    );

                    console.log('Matching availabilities for', dayName, ':', matchingAvailabilities);

                    if (matchingAvailabilities.length > 0) {
                        const allSlots = [];

                        matchingAvailabilities.forEach(avail => {
                            const start = new Date(`2000-01-01 ${avail.start_time}`);
                            const end = new Date(`2000-01-01 ${avail.end_time}`);

                            while (start < end) {
                                const hours = start.getHours();
                                const minutes = start.getMinutes();
                                const ampm = hours >= 12 ? "PM" : "AM";
                                const hour12 = hours % 12 || 12;
                                const minuteStr = minutes.toString().padStart(2, "0");

                                allSlots.push({
                                    value: `${hour12}:${minuteStr} ${ampm}`,
                                    label: `${hour12}:${minuteStr} ${ampm}`,
                                });

                                start.setMinutes(start.getMinutes() + 30);
                            }
                        });

                        setAvailableTimeSlots(
                            sortTimeSlots(allSlots, formData.appointment_date)
                        );
                    } else {
                        setAvailableTimeSlots([
                            {
                                value: "",
                                label: `Not Available on ${dayName}`,
                                isDisabled: true,
                            },
                        ]);
                    }
                } else {
                    setAvailableTimeSlots([
                        {
                            value: "",
                            label: "No availability set",
                            isDisabled: true,
                        },
                    ]);
                }
            } else {
                setAvailableTimeSlots([]);
            }
        } else {
            setAvailableTimeSlots([]);
        }
    }, [selectedDoctor, formData.appointment_date, doctors]);

    useEffect(() => {
        setSidebar(false);
        fetchInitialData();
    }, [AuthToken, setSidebar]);

    const fetchInitialData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments-create`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );

            setPets(response.data.pets || []);
            setDoctors(response.data.doctors || []);
            setBreeds(response.data.breeds || []);

            // Handle customers data properly
            if (response.data.customers) {
                if (Array.isArray(response.data.customers)) {
                    setCustomers(response.data.customers);
                } else if (response.data.customers.data) {
                    setCustomers(response.data.customers.data);
                } else {
                    setCustomers([]);
                }
            } else {
                // Fallback to extracting customers from pets
                const uniqueCustomers = [];
                const customerMap = new Map();
                if (response.data.pets && Array.isArray(response.data.pets)) {
                    response.data.pets.forEach(pet => {
                        if (pet.customer && !customerMap.has(pet.customer.id)) {
                            customerMap.set(pet.customer.id, pet.customer);
                            uniqueCustomers.push(pet.customer);
                        }
                    });
                }
                setCustomers(uniqueCustomers);
            }

            await fetchProceduresAndPayments();
        } catch (err) {
            console.error(err);
            toast.error("Failed to load initial data");
        } finally {
            setLoading(false);
        }
    };

    const fetchProceduresAndPayments = async () => {
        try {
            const [proceduresRes, paymentsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/procedures`, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                }),
                axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                })
            ]);
            setProcedures(proceduresRes.data || []);
            setPaymentMethods(paymentsRes.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPets = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments-create`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setPets(response.data.pets || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );

            // Handle both paginated and non-paginated responses
            let customersData = [];
            if (response.data.customers) {
                if (Array.isArray(response.data.customers)) {
                    customersData = response.data.customers;
                } else if (response.data.customers.data) {
                    customersData = response.data.customers.data;
                }
            } else if (Array.isArray(response.data)) {
                customersData = response.data;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                customersData = response.data.data;
            }

            setCustomers(customersData);
            return customersData;
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const fetchBreeds = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments-create`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setBreeds(response.data.breeds || []);
            return response.data.breeds || [];
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const fetchProceduresList = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/procedures`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setProcedures(response.data || []);
            return response.data || [];
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    // Helper function to get available procedures (excluding already selected ones)
    const getAvailableProcedures = (currentIndex) => {
        const selectedProcedureIds = formData.procedures
            .filter((_, idx) => idx !== currentIndex)
            .map(p => p.procedure_id)
            .filter(id => id);

        return procedures
            .filter(proc => !selectedProcedureIds.includes(proc.id))
            .map(proc => ({
                value: proc.id,
                label: `${proc.name} `
            }));
    };

    // Helper function to get available payment methods (excluding already selected ones)
    const getAvailablePaymentMethods = (currentIndex) => {
        const selectedMethodIds = formData.payments
            .filter((_, idx) => idx !== currentIndex)
            .map(p => p.method_id)
            .filter(id => id);

        return paymentMethods
            .filter(method => !selectedMethodIds.includes(method.id))
            .map(method => ({
                value: method.id,
                label: method.name
            }));
    };

    // Procedure Modal Handlers
    const handleProcedureInputChange = (e) => {
        const { name, value } = e.target;
        setProcedureForm(prev => ({ ...prev, [name]: value }));
        if (procedureErrors[name]) {
            setProcedureErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleAddProcedure = async (e) => {
        e.preventDefault();
        setProcedureSubmitting(true);
        setProcedureErrors({});

        const submitData = {
            name: procedureForm.name,
            price: procedureForm.price
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/procedures`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.procedure) {
                await fetchProceduresList();
                const newProcedureId = response.data.procedure.id;
                const newProcedurePrice = response.data.procedure.price;

                setShowProcedureModal(false);
                setProcedureForm({
                    name: '',
                    price: ''
                });

                toast.success(t('Procedure created successfully!'), {
                    duration: 3000,
                    position: 'top-right',
                });

                // Add the new procedure to the current procedures list
                const currentProcedures = [...formData.procedures];
                const emptyIndex = currentProcedures.findIndex(p => !p.procedure_id);

                if (emptyIndex !== -1) {
                    currentProcedures[emptyIndex].procedure_id = newProcedureId;
                    currentProcedures[emptyIndex].price = parseFloat(newProcedurePrice);
                    setFormData(prev => ({ ...prev, procedures: currentProcedures }));
                    updatePaymentAmounts(currentProcedures, true);
                }
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setProcedureErrors(error.response.data.errors || {});
                toast.error('Validation error occurred');
            } else {
                console.error('Error creating procedure:', error);
                setProcedureErrors({ general: 'Failed to create procedure' });
                toast.error('Failed to create procedure');
            }
        } finally {
            setProcedureSubmitting(false);
        }
    };

    // Breed Modal Handlers
    const handleBreedInputChange = (e) => {
        const { name, value } = e.target;
        setBreedForm(prev => ({ ...prev, [name]: value }));
        if (breedErrors[name]) {
            setBreedErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleAddBreed = async (e) => {
        e.preventDefault();
        setBreedSubmitting(true);
        setBreedErrors({});

        const submitData = {
            name: breedForm.name,
            parent_id: breedForm.parent_id || null
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-breeds`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchBreeds();
                const newBreedId = response.data.data.id;
                setNewlyCreatedBreedId(newBreedId);

                setShowBreedModal(false);
                setBreedForm({
                    name: '',
                    parent_id: ''
                });

                toast.success(t('Breed created successfully!'), {
                    duration: 3000,
                    position: 'top-right',
                });

                setShowPetModal(true);

                setTimeout(() => {
                    setPetForm(prev => ({ ...prev, breed_id: newBreedId }));
                }, 100);
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setBreedErrors(error.response.data.errors || {});
                toast.error('Validation error occurred');
            } else {
                console.error('Error creating breed:', error);
                setBreedErrors({ general: 'Failed to create breed' });
                toast.error('Failed to create breed');
            }
        } finally {
            setBreedSubmitting(false);
        }
    };

    // Pet Modal Handlers
    const handlePetInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPetForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (petErrors[name]) {
            setPetErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleAddPet = async (e) => {
        e.preventDefault();
        setPetSubmitting(true);
        setPetErrors({});

        const submitData = {
            name: petForm.name,
            breed_id: petForm.breed_id,
            customer_id: petForm.customer_id,
            age: petForm.age,
            color: petForm.color,
            male: petForm.gender === 'male' ? 1 : (petForm.gender === 'female' ? 0 : null),
            birth_date: petForm.birth_date,
            is_dead: petForm.is_dead,
            death_date: petForm.is_dead ? petForm.death_date : null
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await fetchPets();
                const newPetId = response.data.data.id;
                setFormData(prev => ({ ...prev, pet_id: newPetId }));
                setShowPetModal(false);
                setPetForm({
                    name: '',
                    breed_id: '',
                    customer_id: '',
                    age: '',
                    color: '',
                    gender: '',
                    birth_date: '',
                    is_dead: false,
                    death_date: ''
                });

                toast.success(t('Pet created successfully!'), {
                    duration: 3000,
                    position: 'top-right',
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {

                setPetErrors(error.response.data.errors || {});

                toast.error(
                    error.response.data.message || 'Validation error occurred'
                );

            } else {

                console.error('Error creating pet:', error);

                setPetErrors({
                    general: error.response?.data?.message || 'Failed to create pet'
                });

                toast.error(
                    error.response?.data?.message || 'Failed to create pet'
                );
            }
        } finally {
            setPetSubmitting(false);
        }
    };

    // Customer Modal Handlers
    const handleCustomerInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerForm(prev => ({ ...prev, [name]: value }));
        if (customerErrors[name]) {
            setCustomerErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleCustomerFileChange = (e) => {
        const file = e.target.files[0];
        setCustomerForm(prev => ({ ...prev, img: file }));
        if (customerErrors.img) {
            setCustomerErrors(prev => ({ ...prev, img: "" }));
        }
    };

    const handleAddCustomer = async (e) => {
        e.preventDefault();
        setCustomerSubmitting(true);
        setCustomerErrors({});

        const formDataToSend = new FormData();
        formDataToSend.append('name', customerForm.name);
        formDataToSend.append('email', customerForm.email);
        formDataToSend.append('phone', customerForm.phone);
        if (customerForm.address) formDataToSend.append('address', customerForm.address);
        if (customerForm.img) formDataToSend.append('img', customerForm.img);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                // Refresh customers list
                const updatedCustomers = await fetchCustomers();

                // Get the new customer ID from response
                let newCustomerId = null;
                if (response.data.customer) {
                    newCustomerId = response.data.customer.id;
                } else if (response.data.data) {
                    newCustomerId = response.data.data.id;
                } else if (response.data.customers?.data?.[0]) {
                    newCustomerId = response.data.customers.data[0].id;
                } else if (updatedCustomers && updatedCustomers.length > 0) {
                    newCustomerId = updatedCustomers[updatedCustomers.length - 1].id;
                }

                if (newCustomerId) {
                    setNewlyCreatedCustomerId(newCustomerId);
                    setShowCustomerModal(false);
                    setCustomerForm({
                        name: '',
                        email: '',
                        phone: '',
                        address: '',
                        img: null
                    });

                    toast.success(t('Customer created successfully!'), {
                        duration: 3000,
                        position: 'top-right',
                    });

                    setShowPetModal(true);

                    setTimeout(() => {
                        setPetForm(prev => ({ ...prev, customer_id: newCustomerId }));
                    }, 100);
                } else {
                    toast.error('Customer created but ID not found');
                }
            } else {
                toast.error(response.data.message || 'Failed to create customer');
            }
        } catch (error) {
            console.error('Error creating customer:', error);

            if (error.response?.status === 400) {
                const errorMessage = error.response.data.message;
                setCustomerErrors({ general: errorMessage });
                toast.error(errorMessage);
            } else if (error.response?.status === 422) {
                const errors = error.response.data.errors || {};
                setCustomerErrors(errors);
                toast.error(error.response.data.message || 'Validation error occurred');
            } else if (error.response?.status === 401) {
                toast.error('Unauthorized. Please login again.');
            } else if (error.response?.status === 500) {
                toast.error('Server error. Please try again later.');
            } else {
                setCustomerErrors({ general: 'Failed to create customer' });
                toast.error(error.response?.data?.message || 'Failed to create customer');
            }
        } finally {
            setCustomerSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
        setSubmitError(null);
    };

    const handleProcedureChange = (index, fieldName, fieldValue) => {
        const updatedProcedures = [...formData.procedures];

        if (fieldName === "procedure_id") {
            updatedProcedures[index].procedure_id = fieldValue;
            const selectedProcedure = procedures.find(p => p.id === fieldValue);
            if (selectedProcedure) {
                updatedProcedures[index].price = selectedProcedure.price;
            }
        } else if (fieldName === "price") {
            updatedProcedures[index].price = parseFloat(fieldValue) || 0;
        } else {
            updatedProcedures[index][fieldName] = parseFloat(fieldValue) || 0;
        }

        setFormData(prev => ({ ...prev, procedures: updatedProcedures }));
        updatePaymentAmounts(updatedProcedures, true);

        if (errors[`procedures.${index}.${fieldName}`]) {
            setErrors(prev => ({ ...prev, [`procedures.${index}.${fieldName}`]: "" }));
        }
        setSubmitError(null);
    };

    const updatePaymentAmounts = (procedures, preserveManualPayments = true) => {
        const newGrandTotal = procedures.reduce((sum, p) => sum + (p.price || 0), 0);

        setFormData(prev => {
            let updatedPayments = [...prev.payments];

            if (preserveManualPayments) {
                let manualTotal = 0;
                updatedPayments.forEach((payment) => {
                    if (payment.manuallyEdited) {
                        manualTotal += payment.amount || 0;
                    }
                });

                let remainingToDistribute = newGrandTotal - manualTotal;

                const nonManualIndices = [];
                updatedPayments.forEach((payment, idx) => {
                    if (!payment.manuallyEdited) {
                        nonManualIndices.push(idx);
                    }
                });

                if (nonManualIndices.length > 0 && remainingToDistribute >= 0) {
                    const amountPerPayment = remainingToDistribute / nonManualIndices.length;
                    nonManualIndices.forEach(idx => {
                        updatedPayments[idx].amount = parseFloat(amountPerPayment);
                    });
                } else if (remainingToDistribute > 0 && updatedPayments.length > 0) {
                    updatedPayments[0].amount = (updatedPayments[0].amount || 0) + remainingToDistribute;
                    updatedPayments[0].manuallyEdited = false;
                }
            } else {
                updatedPayments = updatedPayments.map((payment, idx) => ({
                    ...payment,
                    amount: idx === 0 ? newGrandTotal : 0,
                    manuallyEdited: idx === 0 ? false : payment.manuallyEdited
                }));
            }

            return { ...prev, payments: updatedPayments };
        });
    };

    const handlePaymentChange = (index, fieldName, fieldValue) => {
        if (fieldName === "method_id") {
            const updatedPayments = [...formData.payments];
            updatedPayments[index][fieldName] = parseInt(fieldValue);
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
            if (errors[`payments.${index}.${fieldName}`]) {
                setErrors(prev => ({ ...prev, [`payments.${index}.${fieldName}`]: "" }));
            }
        } else if (fieldName === "amount") {
            const newAmount = parseFloat(fieldValue) || 0;
            const updatedPayments = [...formData.payments];
            const oldAmount = updatedPayments[index].amount || 0;

            updatedPayments[index].amount = newAmount;
            updatedPayments[index].manuallyEdited = true;

            let newTotal = updatedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

            if (newTotal > grandTotal) {
                updatedPayments[index].amount = oldAmount;
                toast.error(`Total payment cannot exceed Grand Total of ${grandTotal}`);
            } else {
                const remainingAmount = grandTotal - newTotal;
                const nonManualIndices = [];

                updatedPayments.forEach((payment, idx) => {
                    if (!payment.manuallyEdited && idx !== index) {
                        nonManualIndices.push(idx);
                    }
                });

                if (nonManualIndices.length > 0 && remainingAmount >= 0) {
                    const amountPerPayment = remainingAmount / nonManualIndices.length;
                    nonManualIndices.forEach(idx => {
                        updatedPayments[idx].amount = parseFloat(amountPerPayment);
                    });
                }
            }

            setFormData(prev => ({ ...prev, payments: updatedPayments }));
            if (errors[`payments.${index}.${fieldName}`]) {
                setErrors(prev => ({ ...prev, [`payments.${index}.${fieldName}`]: "" }));
            }
        }
        setSubmitError(null);
    };

    const handleNoteChange = (index, value) => {
        const updatedNotes = [...formData.notes];
        updatedNotes[index] = value;
        setFormData(prev => ({ ...prev, notes: updatedNotes }));
        setSubmitError(null);
    };

    const addProcedureRow = () => {
        setFormData(prev => ({
            ...prev,
            procedures: [...prev.procedures, { procedure_id: "", price: 0 }]
        }));
    };

    const removeProcedureRow = (index) => {
        const updatedProcedures = formData.procedures.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, procedures: updatedProcedures }));
        updatePaymentAmounts(updatedProcedures, true);
    };

    const addNoteRow = () => {
        setFormData(prev => ({
            ...prev,
            notes: [...prev.notes, ""]
        }));
    };

    const removeNoteRow = (index) => {
        const updatedNotes = formData.notes.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, notes: updatedNotes }));
    };

    const addPaymentRow = () => {
        const usedMethodIds = formData.payments.map(p => p.method_id).filter(id => id);
        const availableMethods = paymentMethods.filter(m => !usedMethodIds.includes(m.id));

        if (availableMethods.length === 0 && paymentMethods.length > 0 && formData.payments.length >= paymentMethods.length) {
            toast.error("No more payment methods available");
            return;
        }

        setFormData(prev => {
            const remaining = grandTotal - totalPaid;
            const updatedPayments = [...prev.payments, {
                method_id: "",
                amount: remaining > 0 ? parseFloat(remaining) : 0,
                manuallyEdited: remaining > 0
            }];

            if (remaining > 0) {
                updatedPayments.forEach((payment, idx) => {
                    if (!payment.manuallyEdited && idx !== updatedPayments.length - 1) {
                        payment.amount = 0;
                    }
                });
            }

            return { ...prev, payments: updatedPayments };
        });
    };

    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) {
            toast.error("At least one payment is required");
            return;
        }

        setFormData(prev => {
            const updatedPayments = prev.payments.filter((_, i) => i !== index);
            const newGrandTotal = grandTotal;
            const manualTotal = updatedPayments.reduce((sum, p) => {
                return sum + (p.manuallyEdited ? (p.amount || 0) : 0);
            }, 0);

            let remainingToDistribute = newGrandTotal - manualTotal;

            const nonManualIndices = [];
            updatedPayments.forEach((payment, idx) => {
                if (!payment.manuallyEdited) {
                    nonManualIndices.push(idx);
                }
            });

            if (nonManualIndices.length > 0 && remainingToDistribute > 0) {
                const amountPerPayment = remainingToDistribute / nonManualIndices.length;
                nonManualIndices.forEach(idx => {
                    updatedPayments[idx].amount = parseFloat(amountPerPayment);
                });
            }

            return { ...prev, payments: updatedPayments };
        });
    };

    const grandTotal = formData.procedures.reduce((sum, p) => sum + (p.price || 0), 0);
    const totalPaid = formData.payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const remainingAmount = grandTotal - totalPaid;

    const handleSubmit = async () => {
        setSubmitting(true);
        setErrors({});
        setSubmitError(null);

        if (!formData.pet_id) {
            toast.error("Please select a pet");
            setSubmitting(false);
            return;
        }
        if (!formData.doctor_id) {
            toast.error("Please select a doctor");
            setSubmitting(false);
            return;
        }
        if (!formData.appointment_date) {
            toast.error("Please select appointment date");
            setSubmitting(false);
            return;
        }
        if (!formData.appointment_time_slot) {
            toast.error("Please select appointment time slot");
            setSubmitting(false);
            return;
        }

        if (formData.procedures.filter(p => p.procedure_id).length === 0) {
            toast.error("Please select at least one procedure");
            setSubmitting(false);
            return;
        }

        if (Math.abs(remainingAmount) > 0.01) {
            toast.error(`Total payment (${totalPaid}) must equal Grand Total (${grandTotal})`);
            setSubmitting(false);
            return;
        }

        // Prepare procedures data with custom prices
        const proceduresData = formData.procedures
            .filter(p => p.procedure_id)
            .map(p => ({
                procedure_id: p.procedure_id,
                price: p.price
            }));

        const submitData = {
            pet_id: formData.pet_id,
            doctor_id: formData.doctor_id,
            appointment_date: formData.appointment_date,
            appointment_time_slot: formData.appointment_time_slot,
            procedure_ids: proceduresData,
            notes: formData.notes.filter(note => note && note.trim() !== ""),
            payments: formData.payments
                .filter(p => p.method_id && p.amount > 0)
                .map(({ manuallyEdited, ...payment }) => payment)
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/appointments`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {
                const appointmentId = response.data.data.id;

                toast.success(t("Appointment created successfully!"), {
                    duration: 3000,
                    position: 'top-right',
                });

                // Get the first procedure name to auto-open its tab
                const selectedProcedures = formData.procedures.filter(p => p.procedure_id);
                let procedureNameToOpen = null;

                if (selectedProcedures.length > 0) {
                    const firstProcedureId = selectedProcedures[0].procedure_id;
                    const procedureObj = procedures.find(p => p.id === firstProcedureId);
                    if (procedureObj) {
                        procedureNameToOpen = procedureObj.name;
                    }
                }

                setTimeout(() => {
                    navigate(`/appointments/${appointmentId}`, {
                        state: { procedureName: procedureNameToOpen }
                    });
                }, 1500);
            } else {
                toast.error(response.data.message || "Failed to create appointment");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                toast.error(err.response.data.message || "Validation error");
            } else if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                console.error(err);
                toast.error("An error occurred while creating the appointment");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getAvailableBreeds = () => {
        const allBreeds = [];

        const mainBreeds = breeds.filter(breed => !breed.parent_id);

        mainBreeds.forEach(breed => {
            allBreeds.push({
                value: breed.id,
                label: breed.name,
                parent: null
            });

            const childBreeds = breeds.filter(child => child.parent_id === breed.id);
            childBreeds.forEach(child => {
                allBreeds.push({
                    value: child.id,
                    label: `${breed.name} - ${child.name}`,
                    parent: breed.name
                });
            });
        });

        const orphanBreeds = breeds.filter(breed =>
            breed.parent_id && !breeds.find(b => b.id === breed.parent_id)
        );
        orphanBreeds.forEach(breed => {
            allBreeds.push({
                value: breed.id,
                label: breed.name,
                parent: null
            });
        });

        return allBreeds;
    };

    const getParentBreedOptions = () => {
        const parentOptions = [];
        breeds.forEach(breed => {
            if (!breed.parent_id) {
                parentOptions.push({
                    value: breed.id,
                    label: breed.name
                });
            }
        });
        return parentOptions;
    };

    if (loading) return <PageLayout><div className="text-center p-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div></PageLayout>;

    const petOptions = pets.map(pet => ({
        value: pet.id,
        label: `🐾  ${pet.name} - 👤 ${pet.customer?.name || ''} - ☎ ${pet.customer?.phone || ''}`
    }));

    const doctorOptions = doctors.map(doctor => ({
        value: doctor.id,
        label: doctor.name
    }));

    const procedureOptions = procedures.map(proc => ({
        value: proc.id,
        label: `${proc.name} `
    }));

    const paymentMethodOptions = paymentMethods.map(method => ({
        value: method.id,
        label: method.name
    }));

    const breedOptions = getAvailableBreeds();
    const parentBreedOptions = getParentBreedOptions();

    const customerOptions = (customers && Array.isArray(customers) && customers.length > 0)
        ? customers.map(customer => ({
            value: customer.id,
            label: `${customer.name} - ${customer.phone || ''}`
        }))
        : [];

    const genderOptions = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' }
    ];

    return (
        <PageLayout>
            <Toaster position="top-right" reverseOrder={false} />
            <Row>
                <Col xl={12}>
                    <Card className="mb-4 p-4">
                        <Card.Body>
                            <ButtonComponent
                                className="btn btn-secondary mb-4"
                                onClick={() => navigate("/appointments")}
                                text={t("Back to Appointments")}
                            />

                            {submitError && (
                                <div className="alert alert-danger mb-4" role="alert">
                                    {submitError}
                                </div>
                            )}

                            {/* Pet Selection */}
                            <Row className="mb-4">
                                <Col xs={12} md={6}>
                                    <LabelFieldComponent
                                        type="date"
                                        label={t("Appointment Date")}
                                        name="appointment_date"
                                        value={formData.appointment_date}
                                        onChange={handleChange}
                                        error={errors.appointment_date}
                                        required
                                        style={{ width: "100%", backgroundColor: "transparent" }}
                                    />
                                </Col>

                                <Col xs={12} md={6}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Pet")} <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={petOptions}
                                                value={petOptions.find(opt => opt.value === formData.pet_id)}
                                                onChange={selected => {
                                                    setFormData(prev => ({ ...prev, pet_id: selected ? selected.value : "" }));
                                                    if (errors.pet_id) setErrors(prev => ({ ...prev, pet_id: "" }));
                                                }}
                                                isClearable
                                                placeholder={t("Select Pet")}
                                                styles={{
                                                    container: provided => ({ ...provided, width: "100%" }),
                                                    control: provided => ({ ...provided, height: "38px" })
                                                }}
                                            />
                                        </div>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary d-flex align-items-center justify-content-center"
                                            onClick={() => setShowPetModal(true)}
                                            style={{ width: "38px", height: "38px", padding: 0 }}
                                            title={t("Add New Pet")}
                                        >
                                            <i className="material-icons" style={{ fontSize: "20px" }}>+</i>
                                        </ButtonComponent>
                                    </div>
                                    {errors.pet_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.pet_id}</span>}
                                </Col>


                            </Row>

                            {/* Doctor and Time Slot */}
                            <Row className="mb-4">
                                <Col xs={12} md={6}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Doctor")} <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Select
                                        options={doctorOptions}
                                        value={doctorOptions.find(opt => opt.value === formData.doctor_id)}
                                        onChange={selected => {
                                            setSelectedDoctor(selected);
                                            setFormData(prev => ({
                                                ...prev,
                                                doctor_id: selected ? selected.value : "",
                                                appointment_time_slot: ""
                                            }));
                                            if (errors.doctor_id) setErrors(prev => ({ ...prev, doctor_id: "" }));
                                        }}
                                        isClearable
                                        placeholder={t("Select Doctor")}
                                        styles={{
                                            container: provided => ({ ...provided, width: "100%" }),
                                            control: provided => ({ ...provided, height: "38px" })
                                        }}
                                    />
                                    {errors.doctor_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.doctor_id}</span>}
                                </Col>

                                <Col xs={12} md={6}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Time Slot")} <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Select
                                        options={availableTimeSlots}
                                        value={availableTimeSlots.find(slot => slot.value === formData.appointment_time_slot)}
                                        onChange={selected => {
                                            setFormData(prev => ({ ...prev, appointment_time_slot: selected ? selected.value : "" }));
                                            if (errors.appointment_time_slot) setErrors(prev => ({ ...prev, appointment_time_slot: "" }));
                                        }}
                                        isClearable
                                        placeholder={t("Select Time Slot")}
                                        isDisabled={!formData.doctor_id || !formData.appointment_date || (availableTimeSlots.length === 1 && availableTimeSlots[0]?.label === "Not Available")}
                                        styles={{
                                            container: provided => ({ ...provided, width: "100%" }),
                                            control: provided => ({ ...provided, height: "38px" }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                color: state.data?.isDisabled ? '#999' : 'inherit',
                                                backgroundColor: state.data?.isDisabled ? '#f5f5f5' : state.isFocused ? '#e0e0e0' : 'white',
                                                cursor: state.data?.isDisabled ? 'not-allowed' : 'pointer'
                                            })
                                        }}
                                        formatOptionLabel={(option) => (
                                            <span style={{ color: option.isDisabled ? '#999' : 'inherit' }}>
                                                {option.label}
                                            </span>
                                        )}
                                    />
                                    {errors.appointment_time_slot && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.appointment_time_slot}</span>}
                                    {formData.doctor_id && formData.appointment_date && availableTimeSlots.length === 0 && (
                                        <span style={{ color: '#ff6b6b', fontSize: '0.8rem', display: 'block', marginTop: '5px' }}>
                                            ⚠️ No availability found for this doctor on selected date
                                        </span>
                                    )}
                                </Col>
                            </Row>

                            {/* Procedures Section */}
                            <h4 className="mb-3">{t("Procedures")} <span style={{ color: 'red' }}>*</span></h4>

                            {formData.procedures.map((proc, index) => (
                                <Card key={index} className="mb-3 p-3" style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
                                    <Row className="align-items-center">
                                        <Col xl={8}>
                                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                                                {t("Procedure")} <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <Select
                                                        options={getAvailableProcedures(index)}
                                                        value={procedureOptions.find(opt => opt.value === proc.procedure_id)}
                                                        onChange={selected => {
                                                            handleProcedureChange(index, 'procedure_id', selected ? selected.value : "");
                                                        }}
                                                        isClearable
                                                        placeholder={t("Select Procedure")}
                                                        styles={{
                                                            container: provided => ({ ...provided, width: "100%" }),
                                                            control: provided => ({ ...provided, height: "38px" })
                                                        }}
                                                    />
                                                </div>
                                                {/* <ButtonComponent
                                                    type="button"
                                                    className="btn btn-primary d-flex align-items-center justify-content-center"
                                                    onClick={() => setShowProcedureModal(true)}
                                                    style={{ width: "38px", height: "38px", padding: 0 }}
                                                    title={t("Add New Procedure")}
                                                >
                                                    <i className="material-icons" style={{ fontSize: "20px" }}>+</i>
                                                </ButtonComponent> */}
                                            </div>
                                            {errors[`procedures.${index}.procedure_id`] && (
                                                <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`procedures.${index}.procedure_id`]}</span>
                                            )}
                                        </Col>

                                        {/* <Col xl={3}>
                                            <LabelFieldComponent
                                                type="number"
                                                label={t("Amount")}
                                                name="price"
                                                value={proc.price}
                                                step="0.01"
                                                min={0}
                                                onChange={(e) => handleProcedureChange(index, 'price', e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    backgroundColor: "white"
                                                }}
                                            />
                                            {errors[`procedures.${index}.price`] && (
                                                <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`procedures.${index}.price`]}</span>
                                            )}
                                        </Col> */}

                                        {/* <Col xl={1} className="d-flex align-items-end mt-3">
                                            <ButtonComponent
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeProcedureRow(index)}
                                                text="X"
                                                disabled={formData.procedures.length === 1}
                                            />
                                        </Col> */}
                                    </Row>
                                </Card>
                            ))}

                            {/* <Row className="mb-4">
                                <Col>
                                    <ButtonComponent
                                        className="btn btn-primary"
                                        onClick={addProcedureRow}
                                        text={t("Add Procedure")}
                                    />
                                </Col>
                            </Row> */}

                            {/* Grand Total, Total Paid and Remaining Section */}
                            {/* <Row className="mb-4">
                                <Col xs={12}>
                                    <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                                        <Row className="justify-content-end text-end">
                                            <Col md={6}>
                                                <div>
                                                    <strong>{t("Grand Total")}: </strong>
                                                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff' }}>
                                                        {grandTotal}
                                                    </span>
                                                </div>

                                                <div className="mt-2">
                                                    <strong>{t("Remaining")}: </strong>
                                                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: remainingAmount < 0 ? '#dc3545' : '#fd7e14' }}>
                                                        {remainingAmount}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row> */}

                            {/* Notes and Payments Section */}
                            {/* <Row className="mb-4">
                                <Col xl={6}>
                                    <h5 className="mb-3">{t("Notes")}</h5>
                                    {formData.notes.map((note, index) => (
                                        <div key={index} className="mb-2 d-flex gap-2">
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={note}
                                                onChange={(e) => handleNoteChange(index, e.target.value)}
                                                placeholder={t("Enter note...")}
                                                style={{ flex: 1 }}
                                            />
                                            <ButtonComponent
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeNoteRow(index)}
                                                text="X"
                                                disabled={formData.notes.length === 1}
                                                style={{ height: "fit-content", marginTop: "auto" }}
                                            />
                                        </div>
                                    ))}
                                    <ButtonComponent
                                        className="btn btn-secondary btn-sm"
                                        onClick={addNoteRow}
                                        text={t("Add Note")}
                                    />
                                </Col>

                                <Col xl={6}>
                                    <h5 className="mb-3">{t("Payments")}</h5>

                                    {formData.payments.map((pay, index) => (
                                        <Card key={index} className="mb-3 p-3" style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
                                            <Row className="align-items-center gx-2">
                                                <Col xs={7}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px' }}>
                                                            {t("Payment Method")}
                                                        </label>
                                                        <Select
                                                            options={getAvailablePaymentMethods(index)}
                                                            value={paymentMethodOptions.find(opt => opt.value === pay.method_id)}
                                                            onChange={selected => {
                                                                handlePaymentChange(index, 'method_id', selected ? selected.value : "");
                                                            }}
                                                            isClearable
                                                            placeholder={t("Select Payment Method")}
                                                            styles={{
                                                                container: provided => ({ ...provided, width: "100%" }),
                                                                control: provided => ({ ...provided, height: "38px" })
                                                            }}
                                                        />
                                                        {errors[`payments.${index}.method_id`] && (
                                                            <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`payments.${index}.method_id`]}</span>
                                                        )}
                                                    </div>
                                                </Col>

                                                <Col xs={4}>
                                                    <LabelFieldComponent
                                                        type="number"
                                                        label={t("Amount")}
                                                        name="amount"
                                                        value={pay.amount}
                                                        step="0.01"
                                                        min={0}
                                                        onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                                                        style={{ width: "100%" }}
                                                    />
                                                    {errors[`payments.${index}.amount`] && (
                                                        <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`payments.${index}.amount`]}</span>
                                                    )}
                                                </Col>

                                                <Col xs={1} className="d-flex justify-content-center">
                                                    <ButtonComponent
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => removePaymentRow(index)}
                                                        text="X"
                                                        disabled={formData.payments.length === 1}
                                                        style={{ marginTop: "20px" }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}
                                    <ButtonComponent
                                        className="btn btn-primary btn-sm mt-1"
                                        onClick={addPaymentRow}
                                        text={t("Add Payment")}
                                        disabled={remainingAmount <= 0}
                                    />
                                </Col>
                            </Row> */}

                            <ButtonComponent
                                style={{ height: "40px" }}
                                className="mc-btn primary w-100 mt-3"
                                onClick={handleSubmit}
                                text={submitting ? t("Creating...") : t("Create Appointment")}
                                disabled={submitting}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Add Pet Modal */}
            <Modal
                show={showPetModal}
                centered
                onHide={() => {
                    setShowPetModal(false);
                    setPetForm({
                        name: '',
                        breed_id: '',
                        customer_id: '',
                        age: '',
                        color: '',
                        gender: '',
                        birth_date: '',
                        is_dead: false,
                        death_date: ''
                    });
                    setPetErrors({});
                    setNewlyCreatedCustomerId(null);
                    setNewlyCreatedBreedId(null);
                }}
                size="lg"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title style={{ fontSize: '20px', fontWeight: 'bold' }}>{t("Add New Pet")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddPet}>
                    <Modal.Body>
                        {petErrors.general && (
                            <div className="alert alert-danger mb-3">{petErrors.general}</div>
                        )}

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t("Name")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={petForm.name}
                                        onChange={handlePetInputChange}
                                        isInvalid={!!petErrors.name}
                                        placeholder={t("Enter pet name")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {petErrors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t("Breed")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={breedOptions}
                                                value={breedOptions.find(opt => opt.value === petForm.breed_id)}
                                                onChange={selected => {
                                                    setPetForm(prev => ({ ...prev, breed_id: selected ? selected.value : "" }));
                                                    if (petErrors.breed_id) setPetErrors(prev => ({ ...prev, breed_id: "" }));
                                                }}
                                                placeholder={t("Select Breed")}
                                                styles={{
                                                    container: provided => ({ ...provided, width: "100%" }),
                                                    control: provided => ({ ...provided, height: "42px" })
                                                }}
                                                formatOptionLabel={(option) => (
                                                    <div>
                                                        {option.parent ? (
                                                            <span>
                                                                <strong>{option.parent}</strong> - {option.label.split(' - ')[1]}
                                                            </span>
                                                        ) : (
                                                            <span>{option.label}</span>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary d-flex align-items-center justify-content-center"
                                            onClick={() => {
                                                setShowPetModal(false);
                                                setShowBreedModal(true);
                                            }}
                                            style={{ width: "42px", height: "42px", padding: 0 }}
                                            title={t("Add New Breed")}
                                        >
                                            +
                                        </ButtonComponent>
                                    </div>
                                    {petErrors.breed_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{petErrors.breed_id}</span>}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t("Customer")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={customerOptions}
                                                value={customerOptions.find(opt => opt.value === petForm.customer_id)}
                                                onChange={selected => {
                                                    setPetForm(prev => ({ ...prev, customer_id: selected ? selected.value : "" }));
                                                    if (petErrors.customer_id) setPetErrors(prev => ({ ...prev, customer_id: "" }));
                                                }}
                                                placeholder={t("Select Customer")}
                                                noOptionsMessage={() => t("No customers found. Please add a customer first.")}
                                                styles={{
                                                    container: provided => ({ ...provided, width: "100%" }),
                                                    control: provided => ({ ...provided, height: "42px" })
                                                }}
                                            />
                                        </div>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary d-flex align-items-center justify-content-center"
                                            onClick={() => {
                                                setShowPetModal(false);
                                                setShowCustomerModal(true);
                                            }}
                                            style={{ width: "42px", height: "42px", padding: 0 }}
                                            title={t("Add New Customer")}
                                        >
                                            +
                                        </ButtonComponent>
                                    </div>
                                    {petErrors.customer_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{petErrors.customer_id}</span>}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">{t("Age")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="age"
                                        value={petForm.age}
                                        onChange={handlePetInputChange}
                                        isInvalid={!!petErrors.age}
                                        placeholder={t("Enter age")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {petErrors.age}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">{t("Color")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="color"
                                        value={petForm.color}
                                        onChange={handlePetInputChange}
                                        isInvalid={!!petErrors.color}
                                        placeholder={t("Enter color")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {petErrors.color}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">{t("Gender")} <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        options={genderOptions}
                                        value={genderOptions.find(opt => opt.value === petForm.gender)}
                                        onChange={selected => {
                                            setPetForm(prev => ({ ...prev, gender: selected ? selected.value : "" }));
                                            if (petErrors.gender) setPetErrors(prev => ({ ...prev, gender: "" }));
                                        }}
                                        placeholder={t("Select Gender")}
                                        styles={{
                                            container: provided => ({ ...provided, width: "100%" }),
                                            control: provided => ({ ...provided, height: "42px" })
                                        }}
                                    />
                                    {petErrors.gender && <span style={{ color: 'red', fontSize: '0.8rem' }}>{petErrors.gender}</span>}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">{t("Birth Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="birth_date"
                                        value={petForm.birth_date}
                                        onChange={handlePetInputChange}
                                        isInvalid={!!petErrors.birth_date}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {petErrors.birth_date}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label={t("Is Dead")}
                                name="is_dead"
                                checked={petForm.is_dead}
                                onChange={handlePetInputChange}
                            />
                        </Form.Group> */}

                        {petForm.is_dead && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">{t("Death Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="death_date"
                                            value={petForm.death_date}
                                            onChange={handlePetInputChange}
                                            isInvalid={!!petErrors.death_date}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {petErrors.death_date}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowPetModal(false);
                                setPetForm({
                                    name: '',
                                    breed_id: '',
                                    customer_id: '',
                                    age: '',
                                    color: '',
                                    gender: '',
                                    birth_date: '',
                                    is_dead: false,
                                    death_date: ''
                                });
                                setPetErrors({});
                            }}
                            text={t("Cancel")}
                        />
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={petSubmitting}
                            text={petSubmitting ? t("Creating...") : t("Create Pet")}
                        />
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Breed Modal */}
            <Modal
                show={showBreedModal}
                centered
                onHide={() => {
                    setShowBreedModal(false);
                    setBreedForm({
                        name: '',
                        parent_id: ''
                    });
                    setBreedErrors({});
                }}
                size="md"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title style={{ fontSize: '20px', fontWeight: 'bold' }}>{t("Add New Breed")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddBreed}>
                    <Modal.Body>
                        {breedErrors.general && (
                            <div className="alert alert-danger mb-3">{breedErrors.general}</div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Breed Name")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={breedForm.name}
                                onChange={handleBreedInputChange}
                                isInvalid={!!breedErrors.name}
                                placeholder={t("Enter breed name")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {breedErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Parent Breed")}
                            </Form.Label>
                            <Select
                                options={parentBreedOptions}
                                value={parentBreedOptions.find(opt => opt.value === breedForm.parent_id)}
                                onChange={selected => {
                                    setBreedForm(prev => ({ ...prev, parent_id: selected ? selected.value : "" }));
                                    if (breedErrors.parent_id) setBreedErrors(prev => ({ ...prev, parent_id: "" }));
                                }}
                                isClearable
                                placeholder={t("Select parent breed (optional)")}
                                styles={{
                                    container: provided => ({ ...provided, width: "100%" }),
                                    control: provided => ({ ...provided, height: "42px" })
                                }}
                            />
                            <small className="text-muted d-block mt-1">
                                {t("If this is a sub-breed, select the main breed. Leave empty for main breed.")}
                            </small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowBreedModal(false);
                                setBreedForm({
                                    name: '',
                                    parent_id: ''
                                });
                                setBreedErrors({});
                            }}
                            text={t("Cancel")}
                        />
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={breedSubmitting}
                            text={breedSubmitting ? t("Creating...") : t("Create Breed")}
                        />
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Customer Modal */}
            <Modal
                show={showCustomerModal}
                centered
                onHide={() => {
                    setShowCustomerModal(false);
                    setCustomerForm({
                        name: '',
                        email: '',
                        phone: '',
                        address: '',
                        img: null
                    });
                    setCustomerErrors({});
                }}
                size="md"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t("Add New Customer")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddCustomer}>
                    <Modal.Body>
                        {customerErrors.general && (
                            <div className="alert alert-danger mb-3">{customerErrors.general}</div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Name")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={customerForm.name}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.name}
                                placeholder={t("Enter customer name")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {customerErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Email")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={customerForm.email}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.email}
                                placeholder={t("Enter email address")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {customerErrors.email}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Phone")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={customerForm.phone}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.phone}
                                placeholder={t("Enter phone number")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {customerErrors.phone}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Address")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="address"
                                rows={2}
                                value={customerForm.address}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.address}
                                placeholder={t("Enter address (optional)")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {customerErrors.address}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Profile Image")}</Form.Label>
                            <Form.Control
                                type="file"
                                name="img"
                                accept="image/*"
                                onChange={handleCustomerFileChange}
                                isInvalid={!!customerErrors.img}
                            />
                            <Form.Control.Feedback type="invalid">
                                {customerErrors.img}
                            </Form.Control.Feedback>
                            <small className="text-muted">
                                {t("Optional. Allowed formats: jpg, jpeg, png")}
                            </small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowCustomerModal(false);
                                setCustomerForm({
                                    name: '',
                                    email: '',
                                    phone: '',
                                    address: '',
                                    img: null
                                });
                                setCustomerErrors({});
                            }}
                            text={t("Cancel")}
                        />
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={customerSubmitting}
                            text={customerSubmitting ? t("Creating...") : t("Create Customer")}
                        />
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Procedure Modal */}
            <Modal
                show={showProcedureModal}
                centered
                onHide={() => {
                    setShowProcedureModal(false);
                    setProcedureForm({
                        name: '',
                        price: ''
                    });
                    setProcedureErrors({});
                }}
                size="md"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title style={{ fontSize: '20px', fontWeight: 'bold' }}>{t("Add New Procedure")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddProcedure}>
                    <Modal.Body>
                        {procedureErrors.general && (
                            <div className="alert alert-danger mb-3">{procedureErrors.general}</div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Procedure Name")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={procedureForm.name}
                                onChange={handleProcedureInputChange}
                                isInvalid={!!procedureErrors.name}
                                placeholder={t("Enter procedure name")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {procedureErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>

                        {/* <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t("Price")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={procedureForm.price}
                                onChange={handleProcedureInputChange}
                                isInvalid={!!procedureErrors.price}
                                placeholder={t("Enter price")}
                                step="0.01"
                                min="0"
                            />
                            <Form.Control.Feedback type="invalid">
                                {procedureErrors.price}
                            </Form.Control.Feedback>
                        </Form.Group> */}
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowProcedureModal(false);
                                setProcedureForm({
                                    name: '',
                                    price: ''
                                });
                                setProcedureErrors({});
                            }}
                            text={t("Cancel")}
                        />
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={procedureSubmitting}
                            text={procedureSubmitting ? t("Creating...") : t("Create Procedure")}
                        />
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}